import React from 'react';
import { WithSearch } from '~/lib/helper/LocationHelper';
import { CancelBlurb } from './CancelBlurb';
import TransactionError from './TransactionError';
import { PaypalCancellationRequest, PaypalCompletionResult } from '~/lib/ApiModel';
import { getAppContext } from '~/lib/AppContext';
import { AsyncStore } from '~/lib/store/AsyncStore';
import { ModalBody, Spinner } from 'reactstrap';
import DialogModal from '~/components/DialogModal';
import WaitForStoreItem from '~/components/WaitForStoreItem';

interface PaypalCancelProps {
    invoiceId?: string;
    transactionId?: string;
    actionId?: string;
    forSignup?: boolean;
}

class PaypalCancelWithParams extends React.Component<PaypalCancelProps> {
    resultStore = new AsyncStore<PaypalCompletionResult, undefined>(
        () =>
            getAppContext().siteApiClient.cancelPaypalPayment({
                transaction_id: this.props.transactionId,
                action_id: this.props.actionId,
            } as PaypalCancellationRequest),
        undefined
    );

    componentDidMount(): void {
        void this.resultStore.load();
    }

    loadingView() {
        return (
            <DialogModal isOpen={true} centered labelledBy="paypal-cancel-title">
                <ModalBody>
                    <div className="d-flex flex-column align-items-center">
                        <Spinner />
                        <div id="paypal-cancel-title" className="mt-3">
                            Cancelling transaction...
                        </div>
                    </div>
                </ModalBody>
            </DialogModal>
        );
    }

    // The landing page keys its wording off paypal_result, so handing it a bare success URL would
    // announce completion to someone who has just cancelled. action_id is left off because the cancelled
    // order is rolled back, so the landing page goes to the server list or the start of signup.
    cancelledReturnUrl(): string | undefined {
        const successUrl = this.resultStore.item?.transaction_success_url;
        if (!successUrl) return undefined;

        // ClassicPanel supplies relative success URLs (e.g. /mpanel/profile/balance). Resolving one
        // against this origin would point at the wrong host, so leave those exactly as they are.
        if (!successUrl.startsWith('http')) return successUrl;

        const url = new URL(successUrl);
        url.searchParams.set('paypal_result', 'cancelled');

        return url.toString();
    }

    render() {
        return (
            <div className="paypalProcess">
                <WaitForStoreItem
                    store={this.resultStore}
                    loading={this.loadingView()}
                    errored={(e) => <TransactionError heading="Transaction Cancellation Error" error={e} />}
                >
                    {() => (
                        <CancelBlurb
                            invoiceId={this.props.invoiceId}
                            returnUrl={this.cancelledReturnUrl()}
                            forSignup={this.props.forSignup}
                        />
                    )}
                </WaitForStoreItem>
            </div>
        );
    }
}

export function PaypalCancel(props: { forSignup?: boolean }) {
    return (
        <WithSearch>
            {(search) => (
                <PaypalCancelWithParams
                    invoiceId={search.get('invoiceid')}
                    transactionId={search.get('transactionid')}
                    actionId={search.get('actionid')}
                    forSignup={props.forSignup}
                />
            )}
        </WithSearch>
    );
}
